import {
  ComponentClass,
  createElement,
  FunctionComponent,
  memo,
  NamedExoticComponent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { Field, FieldProps } from "formik";
import { FieldInputProps, FormikErrors, FormikProps } from "formik/dist/types";
import { PathResult } from "../core/utils/pathBuilder";
import { InputContainerState } from "../ui/inputs/InputContainer/types";
import { genericMemo } from "../utils";
import { useFieldsScopeContext } from "./validation/FieldsScopeContext";
import { DisabledContext } from "./DisabledContext";

export type MimicFieldConfig<V = any, P = any, TForm extends object = {}, TFormattedV = V> = {
  component: string | FunctionComponent<P> | ComponentClass<P, any> | NamedExoticComponent<P>,
  onChangeMimic?: (value: V, setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void) => Promise<void | FormikErrors<TForm>> | void | FormikErrors<TForm>,
  invokeOnChangeMimicFirst?: boolean,
  formatMimic?: (value: V, name: string) => TFormattedV,
  normalizeMimic?: (value: TFormattedV, previousValue: V, allValues: TForm, name: string) => V;
  secondParameterAsValue?: boolean,
  useDisabledContext?: boolean,
} & Omit<P, "name" | "component"> & {
  name: string | PathResult<any>;
};

// todo add ref mapping to field
// todo try to remove any type

export const MimicField = genericMemo(<V, P, TForm extends object = {}, TFormattedV = V>(
  {
    component,
    onChangeMimic,
    invokeOnChangeMimicFirst,
    formatMimic,
    normalizeMimic,
    secondParameterAsValue,
    useDisabledContext = true,
    ...props
  }: MimicFieldConfig<V, P, TForm, TFormattedV>) => {

  const formRef = useRef<FormikProps<TForm>>();

  const fieldRef = useRef<FieldInputProps<V>>();

  const { registerField, unregisterField } = useFieldsScopeContext();

  const mimicFieldName = props.name.hasOwnProperty("path") ? props.name.toString() : props.name as string;

  useEffect(() => {
    registerField(mimicFieldName);
    return () => unregisterField(mimicFieldName);
  }, [ mimicFieldName, registerField, unregisterField ]);

  const wrappedComponent = useMemo(() => memo<FieldProps<V> & { disabled?: boolean }>((
    {
      form,
      field,
      disabled,
      ...props
    }) => {

    const disabledByContext = useContext(DisabledContext);

    formRef.current = form;
    fieldRef.current = field;

    const onChange = useCallback(async (v: any, secondValue: any) => {
      if (secondParameterAsValue) {
        v = secondValue;
      }

      const value = normalizeMimic
        ? normalizeMimic(v as TFormattedV, fieldRef.current!.value, formRef.current!.values, fieldRef.current!.name)
        : v as V;

      if (invokeOnChangeMimicFirst) {
        await onChangeMimic?.(value, formRef.current!.setFieldValue);
        // timeout for updating formik internal state
        await new Promise((resolve) => setTimeout(resolve, 0));
      }
      formRef.current!.setFieldValue(fieldRef.current!.name, value);
      if (!invokeOnChangeMimicFirst) {
        // timeout for updating formik internal state
        await new Promise((resolve) => setTimeout(resolve, 0));
        await onChangeMimic?.(value, formRef.current!.setFieldValue);
      }
    }, []);

    const onBlur = useCallback((v: any) => {
      fieldRef.current!.onBlur(v);
    }, []);

    if (component) {
      const fieldMeta = form.getFieldMeta(field.name);
      const shouldErrorBeShown = form.submitCount > 0 || fieldMeta.touched;

      let fieldError = (typeof fieldMeta.error === "string" ? fieldMeta.error : JSON.stringify(fieldMeta.error));
      if (fieldError?.length === 1) {
        // console.log(`Possible wrong error mapping for key [${field.name}] error [${fieldError}]`);
        fieldError = "";
      }

      return createElement(component as any, {
        value: formatMimic ? formatMimic(field.value, field.name) : field.value,
        onChange: onChange,
        onBlur: onBlur,
        //todo map to new field props
        inputState: (shouldErrorBeShown && Boolean(fieldError)) ? InputContainerState.error : undefined,
        hintText: shouldErrorBeShown
          ? fieldError
          : undefined,
        disabled: disabled || (useDisabledContext ? disabledByContext : false) || form.isSubmitting,
        id: field.name,
        name: field.name,
        ...props,
      } as any);
    }
    return null;
  }), [ component, formatMimic, normalizeMimic, onChangeMimic, invokeOnChangeMimicFirst, secondParameterAsValue, useDisabledContext ]);

  return (
    <Field {...props} component={wrappedComponent} name={mimicFieldName} />
  );
});
